import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "preview", "enhanced", "original", "loading", "error", "actions"]
  static values = {
    url: String
  }

  connect() {
    this.debounceTimer = null
    this.currentEnhancement = null
  }

  // Triggered when user types in the shorthand input
  handleInput(event) {
    const text = event.target.value.trim()

    if (text.length === 0) {
      this.hidePreview()
      return
    }

    // Debounce AI requests
    clearTimeout(this.debounceTimer)

    if (text.length >= 10) { // Only enhance if at least 10 characters
      this.debounceTimer = setTimeout(() => {
        this.enhanceText(text)
      }, 800) // Wait 800ms after user stops typing
    }
  }

  // Manually trigger enhancement
  enhance(event) {
    event.preventDefault()
    const text = this.inputTarget.value.trim()

    if (text.length === 0) {
      this.showError(t("javascript.controllers.ai_comment_controller.enter_text_first"))
      return
    }

    this.enhanceText(text)
  }

  async enhanceText(text) {
    this.showLoading()
    this.hideError()

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ text: text })
      })

      const data = await response.json()

      if (data.success) {
        this.currentEnhancement = data.enhanced
        this.showPreview(data.original, data.enhanced)
      } else {
        this.showError(data.error || t("javascript.controllers.ai_comment_controller.failed_to_enhance"))
      }
    } catch (error) {
      console.error('AI Enhancement Error:', error)
      this.showError(t("javascript.controllers.ai_comment_controller.failed_to_connect"))
    } finally {
      this.hideLoading()
    }
  }

  showPreview(original, enhanced) {
    this.originalTarget.textContent = original
    this.enhancedTarget.textContent = enhanced
    this.previewTarget.classList.remove('hidden')
    this.actionsTarget.classList.remove('hidden')
  }

  hidePreview() {
    this.previewTarget.classList.add('hidden')
    this.actionsTarget.classList.add('hidden')
    this.currentEnhancement = null
  }

  showLoading() {
    this.loadingTarget.classList.remove('hidden')
  }

  hideLoading() {
    this.loadingTarget.classList.add('hidden')
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  hideError() {
    this.errorTarget.classList.add('hidden')
  }

  // Helper: find the Lexical editor controller on the page
  _getLexicalController() {
    const container = document.querySelector('[data-controller*="lexical-editor"]')
    if (!container) return null
    return this.application.getControllerForElementAndIdentifier(container, "lexical-editor")
  }

  // Accept the AI suggestion - insert into Lexical editor
  accept(event) {
    event.preventDefault()

    if (!this.currentEnhancement) return

    const lexical = this._getLexicalController()
    if (lexical) {
      lexical.loadHTML(`<p>${this.currentEnhancement}</p>`)
      this.inputTarget.value = ''
      this.hidePreview()
      lexical.focus()
    }
  }

  // Reject and keep typing
  reject(event) {
    event.preventDefault()
    this.hidePreview()
    this.inputTarget.focus()
  }

  // Skip AI and use original
  skipAi(event) {
    event.preventDefault()

    const originalText = this.inputTarget.value.trim()
    if (!originalText) return

    const lexical = this._getLexicalController()
    if (lexical) {
      lexical.loadHTML(`<p>${originalText}</p>`)
      this.inputTarget.value = ''
      this.hidePreview()
      lexical.focus()
    }
  }
}
